import clsx from 'clsx';
import React from 'react';
import SidebarCrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import { JobsStatusEnum } from '@EHSNavigator/backend';

interface ReviewTaskTableModalProps {
  otherClasses?: string;
  selectedJob: any;
  handleClose: () => void;
  roles: { label: string; value: string }[];
}

const ReviewTaskTableModal: React.FC<ReviewTaskTableModalProps> = ({
  otherClasses,
  selectedJob,
  handleClose,
  roles,
}) => {
  const reviewTaskTableModalClasses = clsx(otherClasses, 'w-full');

  const successData = selectedJob?.data?.returnvalue?.success;
  const failedData = selectedJob?.data?.returnvalue?.failures;

  return (
    <div
      className={reviewTaskTableModalClasses}
      data-testid="review-task-table-modal"
    >
      <div className="mb-8 flex w-full items-center justify-between">
        <h3 className="text-brand-byzantine">Task Review</h3>
        <button className="cursor-pointer" onClick={handleClose}>
          <SidebarCrossIcon className="[&>g>path]:fill-neutral-tones-text h-6 w-6" />
        </button>
      </div>
      <div>
        <div className="mb-4 flex flex-row items-center gap-2">
          <h4 className="text-neutral-tones-text font-inter-variable font-semibold">
            Status:
          </h4>
          <h4
            className={clsx(
              'capitalize',
              selectedJob?.status === JobsStatusEnum.Completed
                ? 'text-brand-papery'
                : selectedJob?.status === JobsStatusEnum.Waiting
                  ? 'text-brand-fair-orange'
                  : selectedJob?.status === JobsStatusEnum.Failed
                    ? 'text-brand-posh-pink'
                    : 'text-brand-byzantine',
            )}
          >
            {selectedJob?.status}
          </h4>
        </div>

        <div className="bg-card-backgrounds/5 border-neutral-tones-text/10 rounded-10 border px-4 py-4">
          <div className="border-b-neutral-tones-text/10 grid grid-cols-2 border-b pb-2.5">
            <p className="font-inter-variable text-neutral-tones-text text-base leading-4 font-semibold">
              Stats
            </p>
            <p className="font-inter-variable text-neutral-tones-text text-base leading-4 font-semibold">
              No of Users
            </p>
          </div>

          <div className="border-b-neutral-tones-text/10 grid grid-cols-2 border-b py-2.5 last:border-b-0 last:pb-0">
            <p className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-semibold">
              Total
            </p>
            <p className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-normal">
              {selectedJob?.data?.users?.length || '---'}
            </p>
          </div>
          <div className="border-b-neutral-tones-text/10 grid grid-cols-2 border-b py-2.5 last:border-b-0 last:pb-0">
            <p className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-semibold">
              Success
            </p>
            <p className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-normal">
              {selectedJob?.data?.returnvalue?.successCount || '---'}
            </p>
          </div>
          <div className="border-b-neutral-tones-text/10 grid grid-cols-2 border-b py-2.5 last:border-b-0 last:pb-0">
            <p className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-semibold">
              Failed
            </p>
            <p className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-normal">
              {selectedJob?.data?.returnvalue?.failedCount || '---'}
            </p>
          </div>
          <div className="border-b-neutral-tones-text/10 grid grid-cols-2 border-b py-2.5 last:border-b-0 last:pb-0">
            <p className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-semibold">
              Reason
            </p>
            <p className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-normal">
              {selectedJob?.failedReason || '---'}
            </p>
          </div>
        </div>
      </div>
      <div className="mt-8">
        <div className="mb-4">
          <h5 className="text-brand-byzantine font-inter-variable mb-1 text-xl font-semibold">
            Successfully Imported Users
          </h5>
          <p className="text-neutral-tones-text/80 font-inter-variable text-sm">
            The following users were imported successfully:
          </p>
        </div>
        <div className="bg-card-backgrounds/5 border-neutral-tones-text/10 rounded-10 global-scrollbar-style overflow-auto border px-4 py-4">
          <div className="w-full min-w-2xl">
            <div className="border-b-neutral-tones-text/10 grid grid-cols-12 gap-4 border-b pb-2.5">
              <p className="font-inter-variable text-neutral-tones-text col-span-3 text-base leading-4 font-semibold">
                Name
              </p>
              <p className="font-inter-variable text-neutral-tones-text col-span-3 text-base leading-4 font-semibold">
                Email
              </p>
              <p className="font-inter-variable text-neutral-tones-text col-span-3 text-base leading-4 font-semibold">
                Department
              </p>
              <p className="font-inter-variable text-neutral-tones-text col-span-3 text-base leading-4 font-semibold">
                Role
              </p>
            </div>

            {successData?.length > 0 ? (
              successData?.map((node: any, index: number) => (
                <div
                  key={index}
                  className="border-b-neutral-tones-text/10 grid grid-cols-12 gap-4 border-b py-2.5 last:border-b-0 last:pb-0"
                >
                  <p className="font-inter-variable text-neutral-tones-text col-span-3 truncate text-sm leading-4 font-normal">
                    {node?.user?.fullName}
                  </p>
                  <a
                    href={`mailto:${node?.user?.email}`}
                    className="font-inter-variable text-brand-byzantine col-span-3 truncate text-sm leading-4 font-semibold underline"
                  >
                    {node?.user?.email}
                  </a>
                  <p className="font-inter-variable text-neutral-tones-text col-span-3 truncate text-sm leading-4 font-normal">
                    {node?.user?.department?.name || '---'}
                  </p>
                  <p className="font-inter-variable text-neutral-tones-text col-span-3 truncate text-sm leading-4 font-normal">
                    {
                      roles?.find((role) => role?.value === node?.user?.roleId)
                        ?.label || 'Admin'
                    }
                  </p>
                </div>
              ))
            ) : (
              <div className="flex items-center gap-2 pt-2.5">
                <WarningIcon className="[&>path]:fill-brand-byzantine h-4 w-4" />
                <p className="font-inter-variable text-brand-byzantine text-sm leading-4 font-semibold">
                  No Records found
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-8">
        <div className="mb-4">
          <h5 className="text-brand-byzantine font-inter-variable mb-1 text-xl font-semibold">
            Failed Import Attempts
          </h5>
          <p className="text-neutral-tones-text/80 font-inter-variable text-sm">
            The following users could not be imported due to errors:
          </p>
        </div>
        <div className="bg-card-backgrounds/5 border-neutral-tones-text/10 rounded-10 border px-4 py-4">
          <div className="border-b-neutral-tones-text/10 grid grid-cols-2 border-b pb-2.5">
            <p className="font-inter-variable text-neutral-tones-text text-base leading-4 font-semibold">
              Email
            </p>
            <p className="font-inter-variable text-neutral-tones-text text-base leading-4 font-semibold">
              Error
            </p>
          </div>

          {failedData?.length > 0 ? (
            failedData?.map((node: any, index: number) => (
              <div
                key={index}
                className="border-b-neutral-tones-text/10 grid grid-cols-2 border-b py-2.5"
              >
                <a
                  href={`mailto:${node?.user?.email}`}
                  className="font-inter-variable text-brand-posh-pink truncate text-sm leading-4 font-semibold underline"
                >
                  {node?.email}
                </a>
                <p className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-normal">
                  {node?.error}
                </p>
              </div>
            ))
          ) : (
            <div className="flex items-center gap-2 pt-2.5">
              <WarningIcon className="[&>path]:fill-brand-byzantine h-4 w-4" />
              <p className="font-inter-variable text-brand-byzantine text-sm leading-4 font-semibold">
                No Records found
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewTaskTableModal;
